import { Button, ButtonLink, Stack } from '@tempots/beatui'
import { attr, computedOf, html, on, TNode, Value, When } from '@tempots/dom'
import { HR } from './hr'
import { Track } from '@/services/track'

export type PlanFeature = {
  text: string
  strikeThrough?: string
}

export type PlanCardData = {
  badge?: {
    text: string
    color: 'primary' | 'secondary' | 'gray'
  }
  planName: string
  monthlyPrice: string | { url: string }
  annualMonthlyPrice: string | { url: string }
  priceUnit: string
  targetUser: string
  url?: string
  linkLabel: string
  description: string
  featuresDescription: string
  features: PlanFeature[]
  comingSoon?: boolean
  highlighted?: boolean
  isMonthly?: Value<boolean>
  ribbon?: TNode
}

export type PlanCardOptions = PlanCardData & {
  isMonthly?: Value<boolean>
}

function getBadgeClasses(color: 'primary' | 'secondary' | 'gray'): string {
  const colorMap = {
    primary: 'bg-orange-700 text-white',
    secondary: 'bg-black text-white',
    gray: 'bg-gray-700 text-white',
  }
  return colorMap[color]
}

export function PlanCard({
  badge,
  planName,
  monthlyPrice,
  annualMonthlyPrice,
  priceUnit,
  targetUser,
  url,
  linkLabel,
  description,
  features,
  featuresDescription,
  comingSoon = false,
  highlighted = false,
  isMonthly = true,
  ribbon,
}: PlanCardOptions) {
  const price = computedOf(
    isMonthly,
    monthlyPrice,
    annualMonthlyPrice
  )((displayMonthly, monthly, annual) => {
    return displayMonthly ? monthly : annual
  })
  return html.div(
    ribbon ? ribbon : null,
    attr.class('w-full sm:w-72 lg:w-64 xl:w-72 relative'),
    attr.class(comingSoon ? 'opacity-70' : ''),
    html.div(
      attr.class(
        'overflow-hidden rounded-xl w-full h-full border bg-white shadow-sm transition-shadow hover:shadow-md'
      ),
      attr.class(highlighted ? 'border-gray-800' : 'border-gray-300'),
      // Badge at top
      badge
        ? html.div(
            attr.class(
              `${getBadgeClasses(badge.color)} px-3 py-1 text-center text-xs font-semibold`
            ),
            badge.text
          )
        : html.div(attr.class('h-6')),
      Stack(
        attr.class('p-4 sm:p-5 md:p-6 gap-2 sm:min-h-100'),
        // Plan name with optional icon
        html.div(
          attr.class('flex items-center gap-2'),
          html.div(
            attr.class('font-semibold text-lg sm:text-xl text-gray-800'),
            comingSoon
              ? html.span(attr.class('text-gray-400'), planName)
              : planName
          )
        ),
        // Price
        html.div(
          attr.class('flex flex-row items-end gap-2'),
          html.div(
            attr.class('text-4xl sm:text-5xl'),
            attr.class(comingSoon ? 'text-gray-300' : 'text-gray-900'),
            When(
              price.map(v => typeof v === 'string'),
              () => html.span(price as Value<string>),
              () =>
                html.img(
                  attr.class('w-14 sm:w-16 h-auto'),
                  attr.src(price.map(v => (v as { url: string }).url))
                )
            )
          ),
          html.div(attr.class('text-xs leading-tight text-gray-600'), priceUnit)
        ),
        // Target user
        html.div(
          attr.class('text-xs font-medium text-gray-600 py-2'),
          targetUser
        ),
        // CTA Button
        html.div(
          attr.class('w-full flex justify-center mb-3'),
          comingSoon
            ? Button(
                {
                  variant: 'filled',
                  color: 'base',
                  disabled: true,
                },
                'Coming soon'
              )
            : ButtonLink(
                {
                  color: 'primary',
                  href: url ?? '#',
                },
                on.click(() =>
                  Track.click({
                    section: 'pricing',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'get_started',
                  })
                ),
                linkLabel
              )
        ),
        // Description
        html.div(
          attr.class('text-sm leading-4 text-gray-600 min-h-12'),
          description
        ),
        // Divider
        HR(),
        // Features list
        html.div(
          attr.class('text-sm'),
          attr.class(comingSoon ? 'text-gray-500' : 'text-gray-700'),
          html.div(attr.class('font-sm mb-2'), featuresDescription),
          html.ul(
            // attr.class('space-y-1'),
            ...features
              .slice(features[0].text.startsWith('Includes') ? 1 : 0)
              .map(feature =>
                html.li(
                  attr.class('flex items-start relative'),

                  html.span(attr.class('mr-2'), '•'),
                  html.span(
                    attr.class(
                      feature.strikeThrough
                        ? 'line-through decoration-red-700'
                        : null
                    ),
                    feature.text
                  ),
                  feature.strikeThrough
                    ? html.span(
                        attr.class(
                          'text-xs text-red-700 font-semibold absolute text-center right-0 bottom-4 max-w-24 rotate-5'
                        ),
                        feature.strikeThrough
                      )
                    : null
                )
              )
          )
        )
      )
    )
  )
}
